// جست‌وجو/فیلترِ خودکارِ ستونی برای GxTable — الگوی «مرور حساب‌ها»:
// هر ستون یک قیف می‌گیرد؛ ستون‌های متنی «شاملِ…» و ستون‌های عددی «از/تا».
// نوعِ ستون از روی نمونهٔ داده حدس زده می‌شود (قابلِ override با gxFilter).
// gxFilter: "text" | "num" | "date" | "dateRange" | "enum" | "bool" | false
//
// نوع اگر صریح داده نشود از روی خودِ داده تشخیص داده می‌شود (inferMode) — ولی
// محافظه‌کارانه: ستونی که محتوایش متن است هیچ‌وقت «بازهٔ عددی» نمی‌گیرد.
import React from "react";
import { Input, InputNumber, Button, Space, Checkbox } from "antd";
import { SearchOutlined, FilterFilled } from "@ant-design/icons";
import { JalaliDateInput } from "../utils/dateUtils";
import { faNum } from "../utils/faNumberFormat";

// نرمال‌سازی برای تطبیقِ فارسی: ارقامِ فارسی/عربی→لاتین، حذفِ جداکننده‌ها، ی/ک عربی
export function faNorm(v) {
  return String(v ?? "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
    .replace(/ي/g, "ی").replace(/ك/g, "ک")
    .replace(/[,،٬‌]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase().trim();
}

// عددِ خام از یک مقدار (برای فیلترِ بازه)
function toNum(v) {
  if (v == null || v === "") return null;
  const s = faNorm(v).replace(/[^\d.\-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) && s !== "" ? n : null;
}

// مقدارِ سلول از dataIndex یا gxFilterGet
function cellValue(record, dataIndex) {
  if (dataIndex == null) return undefined;
  if (Array.isArray(dataIndex)) return dataIndex.reduce((o, k) => (o == null ? o : o[k]), record);
  return record?.[dataIndex];
}

function filterCell(col, record) {
  if (typeof col.gxFilterGet === "function") {
    try { return col.gxFilterGet(record); } catch { return undefined; }
  }
  return cellValue(record, col.dataIndex);
}

// نمونه‌گیری از مقادیرِ ناتهیِ یک ستون
function sampleValues(col, data, limit = 40) {
  const out = [];
  for (const row of (data || []).slice(0, limit)) {
    const v = filterCell(col, row);
    if (v == null || v === "") continue;
    out.push(v);
  }
  return out;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * نوعِ فیلترِ یک ستون. ترتیب مهم است — از صریح‌ترین نشانه به مبهم‌ترین.
 *
 * 🐞 قبلاً فقط «عددی یا متنی» حدس زده می‌شد و ملاکش این بود که مقدار *شبیهِ* عدد
 * باشد. برای ستون‌هایی مثل «بخش» که مقدارِ خامشان id است (ولی متن نشان می‌دهند)
 * بازهٔ عددیِ بی‌معنی می‌آمد. حالا id/enum از عدد جدا می‌شود.
 */
export function inferMode(col, data) {
  if (col.gxFilter) return col.gxFilter;

  // نشانهٔ صریح از خودِ ستون (همان چیزی که برای ویرایش تعریف شده)
  const byEdit = { boolean: "bool", date: "dateRange", number: "num", select: "enum" };
  if (col.editType && byEdit[col.editType]) return byEdit[col.editType];

  const vals = sampleValues(col, data);
  if (!vals.length) return "text";

  if (vals.every((v) => typeof v === "boolean")) return "bool";
  if (vals.every((v) => ISO_DATE.test(String(v)))) return "dateRange";

  // کم‌تنوع ⇒ دسته‌ای (چک‌باکسِ چندانتخابی) — «بخش»، «واحد»، «نوع حقوق»…
  const distinct = new Set(vals.map((v) => String(v)));
  if (distinct.size <= 12 && vals.length >= 4 && distinct.size < vals.length) return "enum";

  // فقط عددِ واقعی (نه رشتهٔ عددنما) بازهٔ عددی می‌گیرد
  if (vals.every((v) => typeof v === "number")) return "num";

  return "text";
}

const faFmt = (n) => (n == null ? "" : faNum(Number(n)));

function filterActions({ confirm, clearFilters }) {
  return (
    <Space>
      <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => confirm()}>فیلتر</Button>
      <Button size="small" onClick={() => { clearFilters && clearFilters(); confirm(); }}>پاک‌کردن</Button>
    </Space>
  );
}

// دراپ‌داونِ جست‌وجوی متنی (شاملِ…)
function textDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
  return (
    <div style={{ padding: 8, direction: "rtl" }} onKeyDown={(e) => e.stopPropagation()}>
      <Input autoFocus placeholder="جست‌وجو…" value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()} style={{ width: 190, marginBottom: 8, display: "block" }} />
      {filterActions({ confirm, clearFilters })}
    </div>
  );
}

// دراپ‌داونِ بازهٔ عددی (از/تا)
function rangeDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
  const cur = selectedKeys[0] || {};
  const set = (patch) => {
    const next = { ...cur, ...patch };
    setSelectedKeys(next.min != null || next.max != null ? [next] : []);
  };
  return (
    <div style={{ padding: 8, direction: "rtl" }} onKeyDown={(e) => e.stopPropagation()}>
      <Space direction="vertical" size={6}>
        <InputNumber placeholder="از (کمینه)" style={{ width: 190 }} controls={false}
          formatter={faFmt} value={cur.min ?? null} onChange={(v) => set({ min: v })} />
        <InputNumber placeholder="تا (بیشینه)" style={{ width: 190 }} controls={false}
          formatter={faFmt} value={cur.max ?? null} onChange={(v) => set({ max: v })} />
        {filterActions({ confirm, clearFilters })}
      </Space>
    </div>
  );
}

// تاریخ دقیق
function dateDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
  const cur = selectedKeys[0] || null;
  return (
    <div style={{ padding: 8, direction: "rtl" }} onKeyDown={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: 8 }}>
        <JalaliDateInput value={cur} onChange={(v) => setSelectedKeys(v ? [v] : [])} />
      </div>
      {filterActions({ confirm, clearFilters })}
    </div>
  );
}

// بازه تاریخ
function dateRangeDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
  const cur = selectedKeys[0] || {};
  const set = (patch) => {
    const next = { ...cur, ...patch };
    setSelectedKeys(next.from || next.to ? [next] : []);
  };
  return (
    <div style={{ padding: 8, direction: "rtl" }} onKeyDown={(e) => e.stopPropagation()}>
      <Space direction="vertical" size={6}>
        <div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>از تاریخ</div>
          <JalaliDateInput value={cur.from || null} onChange={(v) => set({ from: v })} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>تا تاریخ</div>
          <JalaliDateInput value={cur.to || null} onChange={(v) => set({ to: v })} />
        </div>
        {filterActions({ confirm, clearFilters })}
      </Space>
    </div>
  );
}

// بولی: دارد / ندارد / همه
function boolDropdown(labels) {
  const yes = labels?.yes || "دارد";
  const no = labels?.no || "ندارد";
  return function BoolFilterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
    const cur = selectedKeys[0];
    const pick = (v) => { setSelectedKeys(v === null ? [] : [v]); confirm(); };
    const item = (label, v) => (
      <div style={{ padding: "2px 0" }}>
        <Checkbox checked={cur === v} onChange={() => pick(cur === v ? null : v)}>{label}</Checkbox>
      </div>
    );
    return (
      <div style={{ padding: 8, direction: "rtl", minWidth: 150 }} onKeyDown={(e) => e.stopPropagation()}>
        {item(yes, true)}
        {item(no, false)}
        <div style={{ marginTop: 6 }}>{filterActions({ confirm, clearFilters })}</div>
      </div>
    );
  };
}

/** گزینه‌های enum از داده یا gxFilterOptions */
export function collectEnumOptions(dataIndex, data, explicit, getVal) {
  if (Array.isArray(explicit) && explicit.length) {
    return explicit.map((o) => (typeof o === "object"
      ? { text: o.text ?? o.label ?? String(o.value), value: o.value }
      : { text: String(o), value: o }));
  }
  const seen = new Map();
  for (const row of data || []) {
    const v = typeof getVal === "function" ? getVal(row) : cellValue(row, dataIndex);
    if (v == null || v === "") continue;
    const key = String(v);
    if (!seen.has(key)) seen.set(key, v);
  }
  return [...seen.entries()]
    .sort((a, b) => String(a[0]).localeCompare(String(b[0]), "fa"))
    .map(([, v]) => ({ text: String(v), value: v }));
}

function enumDropdown(options) {
  return function EnumFilterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
    const selected = Array.isArray(selectedKeys) ? selectedKeys : [];
    const toggle = (val, checked) => {
      if (checked) setSelectedKeys([...selected, val]);
      else setSelectedKeys(selected.filter((x) => x !== val));
    };
    return (
      <div style={{ padding: 8, direction: "rtl", maxWidth: 260 }} onKeyDown={(e) => e.stopPropagation()}>
        <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 8 }}>
          {options.length === 0 && (
            <div style={{ color: "#999", fontSize: 12, padding: 4 }}>مقداری نیست</div>
          )}
          {options.map((o) => (
            <div key={String(o.value)} style={{ padding: "2px 0" }}>
              <Checkbox
                checked={selected.includes(o.value)}
                onChange={(e) => toggle(o.value, e.target.checked)}
              >
                {o.text}
              </Checkbox>
            </div>
          ))}
        </div>
        {filterActions({ confirm, clearFilters })}
      </div>
    );
  };
}

const filterIcon = (filtered) => <SearchOutlined style={{ color: filtered ? "var(--brand, #1F4E79)" : undefined }} />;
const filterIconActive = (filtered) => (filtered ? <FilterFilled style={{ color: "var(--brand, #1F4E79)" }} /> : <SearchOutlined />);

// یک ستون را با قیفِ جست‌وجو غنی می‌کند (اگر واجدِ شرایط باشد)
export function enhanceColumnFilter(col, data) {
  // انصراف: صریحاً خاموش، بدونِ dataIndex (مگر enum با gxFilterGet)، ستونِ اکشن، یا از قبل فیلتردار
  if (!col || col.gxFilter === false) return col;
  if (col.filters || col.filterDropdown || col.onFilter) return col;
  const di = col.dataIndex;
  const hasGet = typeof col.gxFilterGet === "function";
  if (di == null && col.gxFilter !== "enum" && !hasGet) return col;

  const mode = inferMode(col, data);
  const read = (record) => filterCell(col, record);

  if (mode === "bool") {
    return {
      ...col,
      filterDropdown: boolDropdown(col.gxFilterLabels),
      filterIcon: filterIconActive,
      onFilter: (value, record) => Boolean(read(record)) === Boolean(value),
    };
  }

  if (mode === "num") {
    return {
      ...col,
      filterDropdown: rangeDropdown,
      filterIcon: filterIconActive,
      onFilter: (value, record) => {
        const n = toNum(read(record));
        if (n == null) return false;
        const { min, max } = value || {};
        if (min != null && n < min) return false;
        if (max != null && n > max) return false;
        return true;
      },
    };
  }

  if (mode === "date") {
    return {
      ...col,
      filterDropdown: dateDropdown,
      filterIcon: filterIconActive,
      onFilter: (value, record) => {
        if (!value) return true;
        const cell = read(record);
        if (cell == null) return false;
        return String(cell).slice(0, 10) === String(value).slice(0, 10);
      },
    };
  }

  if (mode === "dateRange") {
    return {
      ...col,
      filterDropdown: dateRangeDropdown,
      filterIcon: filterIconActive,
      onFilter: (value, record) => {
        const { from, to } = value || {};
        const cell = read(record);
        if (cell == null || cell === "") return false;
        const d = String(cell).slice(0, 10);
        if (from && d < String(from).slice(0, 10)) return false;
        if (to && d > String(to).slice(0, 10)) return false;
        return true;
      },
    };
  }

  if (mode === "enum") {
    const opts = collectEnumOptions(di, data, col.gxFilterOptions, hasGet ? col.gxFilterGet : null);
    return {
      ...col,
      filterDropdown: enumDropdown(opts),
      filterIcon: filterIconActive,
      onFilter: (value, record) => {
        const cell = read(record);
        if (Array.isArray(value)) return value.map(String).includes(String(cell));
        return String(cell) === String(value);
      },
    };
  }

  return {
    ...col,
    filterDropdown: textDropdown,
    filterIcon,
    onFilter: (value, record) => faNorm(read(record)).includes(faNorm(value)),
  };
}
